import type {
  GetKmsDocumentStatusResponse,
  HitlCandidate,
} from '~/app/services/kms/kms-document.types';

export const KMS_MAX_FILE_MB = 50;
export const KMS_MAX_FILE_BYTES = KMS_MAX_FILE_MB * 1024 * 1024;

export const KMS_ACCEPT =
  '.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.json,.png,.jpg,.jpeg,.webp,.tiff,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/plain,text/csv,application/json,image/png,image/jpeg,image/webp,image/tiff';

export const KMS_SUPPORTED_MIME_TYPES = new Set([
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'text/plain',
  'text/csv',
  'application/json',
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/webp',
  'image/tiff',
]);

const EXTENSION_MIME: Record<string, string> = {
  '.pdf': 'application/pdf',
  '.doc': 'application/msword',
  '.docx':
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.xls': 'application/vnd.ms-excel',
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  '.txt': 'text/plain',
  '.csv': 'text/csv',
  '.json': 'application/json',
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.webp': 'image/webp',
  '.tiff': 'image/tiff',
  '.tif': 'image/tiff',
};

export function resolveKmsMimeType(file: File): string {
  if (file.type) {
    return file.type.toLowerCase();
  }
  const ext = file.name.includes('.')
    ? `.${file.name.split('.').pop()!.toLowerCase()}`
    : '';
  return EXTENSION_MIME[ext] ?? '';
}

export function isSupportedKmsFile(file: File): boolean {
  const mime = resolveKmsMimeType(file);
  return Boolean(mime) && KMS_SUPPORTED_MIME_TYPES.has(mime);
}

export function isTerminalKmsStatus(status: string): boolean {
  return (
    status === 'linked' ||
    status === 'pending_review' ||
    status === 'index_failed' ||
    status === 'write_failed'
  );
}

export function parseAssetIdFromKmsScope(scope?: string): string | undefined {
  if (!scope?.startsWith('asset:')) {
    return undefined;
  }
  const assetId = scope.slice('asset:'.length).trim();
  return assetId || undefined;
}

export function isOrgWideKmsScope(scope?: string): boolean {
  if (!scope) {
    return true;
  }
  return scope === 'global' || scope.startsWith('site:');
}

export function buildKmsScope(params: {
  kind: 'global' | 'site' | 'asset';
  siteId?: string;
  assetId?: string;
}): string {
  if (params.kind === 'global') {
    return 'global';
  }
  if (params.kind === 'site' && params.siteId) {
    return `site:${params.siteId}`;
  }
  if (params.kind === 'asset' && params.assetId) {
    return `asset:${params.assetId}`;
  }
  return 'global';
}

const KMS_NON_LINKABLE_DOCUMENT_TYPES = new Set(['media', 'structured_data']);

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isAssetUuid(value: string): boolean {
  return UUID_RE.test(value.trim());
}

/** Confirmed document type: manual hint wins over AI classification. */
export function resolveKmsConfirmedDocumentType(
  document:
    | Pick<
        GetKmsDocumentStatusResponse,
        'documentTypeHint' | 'classifiedDocumentType'
      >
    | null
    | undefined,
): string | null {
  const hint = document?.documentTypeHint?.trim();
  if (hint) {
    return hint;
  }
  const classified = document?.classifiedDocumentType?.trim();
  return classified || null;
}

export function isKmsNonLinkableDocumentType(documentType: string): boolean {
  return KMS_NON_LINKABLE_DOCUMENT_TYPES.has(documentType.trim().toLowerCase());
}

/** AssetLinker never runs for platform docs or media / structured_data types. */
export function isKmsAssetLinkSkipped(
  document:
    | Pick<
        GetKmsDocumentStatusResponse,
        'organizationId' | 'documentTypeHint' | 'classifiedDocumentType'
      >
    | null
    | undefined,
): boolean {
  if (!document?.organizationId) {
    return true;
  }
  const documentType = resolveKmsConfirmedDocumentType(document);
  if (!documentType) {
    return false;
  }
  return isKmsNonLinkableDocumentType(documentType);
}

/** Classifier document-type suggestions from step 1 — never AssetLinker assets. */
export function resolveKmsTypeCandidates(
  document:
    | Pick<GetKmsDocumentStatusResponse, 'classificationCandidates'>
    | null
    | undefined,
): HitlCandidate[] {
  if (!document) {
    return [];
  }
  return normalizeHitlCandidates(document.classificationCandidates)
    .filter((candidate) => candidate.kind === 'type' && candidate.documentType)
    .map((candidate) => ({
      ...candidate,
      label: formatKmsDocumentTypeLabel(candidate.documentType),
    }));
}

export function shouldShowKmsTypeCandidates(
  document: GetKmsDocumentStatusResponse | null,
): boolean {
  return resolveKmsTypeCandidates(document).length > 0;
}

export function normalizeHitlCandidates(raw: unknown): HitlCandidate[] {
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw.slice(0, 10).map((entry, index) => {
    if (!entry || typeof entry !== 'object') {
      return {
        key: `candidate-${index}`,
        assetId: null,
        documentType: null,
        score: 0,
        label: `Candidate ${index + 1}`,
        kind: 'type' as const,
      };
    }

    const record = entry as Record<string, unknown>;
    const assetId =
      (typeof record.asset_id === 'string' && record.asset_id) ||
      (typeof record.assetId === 'string' && record.assetId) ||
      null;
    const documentType =
      (typeof record.document_type === 'string' && record.document_type) ||
      (typeof record.documentType === 'string' && record.documentType) ||
      null;
    const scoreRaw = record.score ?? record.confidence ?? 0;
    const score =
      typeof scoreRaw === 'number' ? scoreRaw : Number(scoreRaw) || 0;
    const reason =
      typeof record.reason === 'string'
        ? record.reason
        : typeof record.match_reason === 'string'
          ? record.match_reason
          : typeof record.matchReason === 'string'
            ? record.matchReason
            : undefined;
    const kind: HitlCandidate['kind'] =
      assetId && !documentType ? 'asset' : 'type';
    const label =
      (typeof record.label === 'string' && record.label) ||
      (typeof record.name === 'string' && record.name) ||
      documentType ||
      assetId ||
      `Candidate ${index + 1}`;

    return {
      key: `${assetId ?? documentType ?? 'c'}-${index}`,
      assetId,
      documentType,
      score,
      label,
      reason,
      kind,
    };
  });
}

/** AssetLinker (`kms_linker.proto`) candidates — always assets, never document types. */
export function normalizeAssetLinkCandidates(raw: unknown): HitlCandidate[] {
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw
    .slice(0, 20)
    .map((entry, index) => {
      if (!entry || typeof entry !== 'object') {
        return null;
      }
      const record = entry as Record<string, unknown>;
      const assetId =
        (typeof record.asset_id === 'string' && record.asset_id.trim()) ||
        (typeof record.assetId === 'string' && record.assetId.trim()) ||
        '';
      if (!assetId || !isAssetUuid(assetId)) {
        return null;
      }
      const scoreRaw = record.score ?? record.confidence ?? 0;
      const score =
        typeof scoreRaw === 'number' ? scoreRaw : Number(scoreRaw) || 0;
      const reason =
        typeof record.reason === 'string'
          ? record.reason
          : typeof record.match_reason === 'string'
            ? record.match_reason
            : typeof record.matchReason === 'string'
              ? record.matchReason
              : undefined;

      return {
        key: `${assetId}-${index}`,
        assetId,
        documentType: null,
        score,
        label: assetId,
        reason,
        kind: 'asset' as const,
      };
    })
    .filter(
      (candidate): candidate is NonNullable<typeof candidate> =>
        candidate != null,
    );
}

/** AssetLinker output only — never classifier document-type candidates. */
export function resolveKmsLinkerCandidates(document: {
  linkCandidates?: unknown;
}): unknown {
  if (document.linkCandidates == null) {
    return [];
  }
  if (!Array.isArray(document.linkCandidates)) {
    return [];
  }
  if (document.linkCandidates.length === 0) {
    return [];
  }
  return normalizeAssetLinkCandidates(document.linkCandidates).length > 0
    ? document.linkCandidates
    : [];
}

/** True when HITL is for AssetLinker (assets), not KMS Classify (document types). */
export function isAssetLinkHitlReview(params: {
  linkCandidates?: unknown;
  linkScope?: string | null;
}): boolean {
  if (params.linkScope?.trim()) {
    return true;
  }
  return normalizeAssetLinkCandidates(params.linkCandidates).length > 0;
}

export function enrichHitlCandidateLabels(
  candidates: HitlCandidate[],
  assetNameById: ReadonlyMap<string, string>,
): HitlCandidate[] {
  return candidates.map((candidate) => {
    if (!candidate.assetId) {
      return candidate;
    }
    const assetName = assetNameById.get(candidate.assetId);
    if (!assetName) {
      return candidate;
    }
    return {
      ...candidate,
      label: assetName,
    };
  });
}

export function formatConfidencePercent(
  value: number | null | undefined,
): string {
  if (value == null || Number.isNaN(value)) {
    return '—';
  }
  const pct = value <= 1 ? value * 100 : value;
  return `${Math.round(pct)}%`;
}

const KMS_DOCUMENT_TYPE_LABELS: Record<string, string> = {
  technical_manual: 'Technical manual',
  maintenance_reports: 'Maintenance report',
  maintenance_contracts: 'Maintenance contract',
  asset_info_sheets: 'Asset info sheet',
  compliance_doc: 'Compliance document',
  media: 'Media',
  structured_data: 'Structured data',
  other: 'Other',
};

/** Human-readable label for a KMS document type code (falls back to raw value). */
export function formatKmsDocumentTypeLabel(
  type: string | null | undefined,
): string {
  if (!type?.trim()) {
    return '—';
  }
  const key = type.trim();
  return KMS_DOCUMENT_TYPE_LABELS[key] ?? key.replace(/_/g, ' ');
}

export function formatKmsFileSize(bytes: number | null | undefined): string {
  if (bytes == null || Number.isNaN(bytes) || bytes < 0) {
    return '—';
  }
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function buildKmsDocumentDetailPath(
  documentId: string,
  organizationId?: string | null,
): string {
  const base = `/kms/documents/${documentId}`;
  if (organizationId === null || organizationId === 'platform') {
    return `${base}?organizationId=platform`;
  }
  if (organizationId) {
    return `${base}?organizationId=${encodeURIComponent(organizationId)}`;
  }
  return base;
}

export function isAwaitingKmsExtraction(
  document:
    | Pick<
        GetKmsDocumentStatusResponse,
        | 'status'
        | 'extractedFields'
        | 'fieldsConfirmedAt'
        | 'classifiedDocumentType'
      >
    | null
    | undefined,
): boolean {
  if (!document?.classifiedDocumentType) {
    return false;
  }
  if (document.extractedFields != null || document.fieldsConfirmedAt) {
    return false;
  }
  return document.status === 'linked' || document.status === 'processing';
}

/** True while StructuredExtractor has not persisted fields yet. */
export function isAwaitingKmsLink(
  document:
    | Pick<
        GetKmsDocumentStatusResponse,
        | 'organizationId'
        | 'status'
        | 'extractedFields'
        | 'linkScope'
        | 'classifiedDocumentType'
        | 'documentTypeHint'
      >
    | null
    | undefined,
): boolean {
  if (isKmsAssetLinkSkipped(document)) {
    return false;
  }
  if (!document?.organizationId || !resolveKmsConfirmedDocumentType(document)) {
    return false;
  }
  if (document.linkScope != null) {
    return false;
  }
  if (document.extractedFields == null) {
    return false;
  }
  return document.status === 'linked' || document.status === 'pending_review';
}

/** True when AssetLinker (step 3) can be queued manually via the API. */
export function canRerunKmsAssetLink(
  document:
    | Pick<
        GetKmsDocumentStatusResponse,
        | 'organizationId'
        | 'status'
        | 'extractedFields'
        | 'classifiedDocumentType'
        | 'documentTypeHint'
      >
    | null
    | undefined,
): boolean {
  if (isKmsAssetLinkSkipped(document)) {
    return false;
  }
  if (!document?.organizationId) {
    return false;
  }
  if (document.status === 'processing') {
    return false;
  }
  if (!resolveKmsConfirmedDocumentType(document)) {
    return false;
  }
  if (document.extractedFields == null) {
    return false;
  }
  return document.status === 'linked' || document.status === 'pending_review';
}

export function isOlderThanHours(isoDate: string, hours: number): boolean {
  const ts = Date.parse(isoDate);
  if (Number.isNaN(ts)) {
    return false;
  }
  return Date.now() - ts > hours * 60 * 60 * 1000;
}
